import { useState } from 'react';
import SEOMeta from '../../components/feature/SEOMeta';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactMethods = [
  { icon: 'ri-telegram-line', label: 'Telegram', value: '@haykagab', href: 'https://t.me/haykagab', color: 'text-blue-500', bg: 'bg-blue-50' },
  { icon: 'ri-mail-line', label: 'Email', value: 'haykagab@gmail.com', href: 'mailto:haykagab@gmail.com', color: 'text-blue-600', bg: 'bg-blue-50' },
];

function ContactHero() {
  const { t } = useTranslation();
  return (
    <section
      className="relative pt-40 pb-20"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/fc8956ac4d5f941e8b88edf3aec2ab95.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/55 to-gray-900/40"></div>
      <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <nav className="flex items-center justify-center gap-2 text-sm text-white/60 mb-8">
          <a href="/" className="hover:text-white cursor-pointer transition-colors duration-200">{t('contact.heroNav')}</a>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-white/90">{t('contact.heroNavCurrent')}</span>
        </nav>
        <h1 className="text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
          {t('contact.heroTitle')}
        </h1>
        <p className="text-base text-white/75 max-w-2xl mx-auto leading-relaxed">
          {t('contact.heroDesc')}
        </p>
        <div className="mt-5 w-24 h-1 bg-blue-500 mx-auto rounded-full"></div>
      </div>
    </section>
  );
}

function ContactSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const serviceOptions = [
    t('contact.opt1'),
    t('contact.opt2'),
    t('contact.opt3'),
    t('contact.opt4'),
    t('contact.opt5'),
    t('contact.opt6'),
    t('contact.opt7'),
    t('contact.opt8'),
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    contact: '',
    email: '',
    service: '',
    comment: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.fullName || !formData.contact || !formData.email || !formData.service) return;
    if (formData.comment.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      body.append('fullName', formData.fullName);
      body.append('contact', formData.contact);
      body.append('email', formData.email);
      body.append('service', formData.service);
      body.append('comment', formData.comment);
      const res = await fetch('https://readdy.ai/api/form/d71die3k1jkj467r5e3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ fullName: '', contact: '', email: '', service: '', comment: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t('contact.reachTitle')}
            </h2>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">{t('contact.reachDesc')}</p>

            <div className="flex flex-col gap-4 mb-10">
              {contactMethods.map((method) => (
                <a
                  key={method.label}
                  href={method.href}
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-blue-200 group cursor-pointer transition-all duration-200"
                  rel="noopener noreferrer"
                >
                  <div className={`w-10 h-10 flex items-center justify-center ${method.bg} rounded-xl flex-shrink-0`}>
                    <i className={`${method.icon} ${method.color} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">{method.label}</p>
                    <p className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors duration-200">{method.value}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{t('contact.officeTitle')}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{t('contact.officeLocation')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('contact.officeNote')}</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-time-line text-gray-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{t('contact.responseNote')}</p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-100 rounded-2xl p-8" style={{ boxShadow: '0 2px 20px 0 rgba(0,0,0,0.05)' }}>
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t('contact.formTitle')}
              </h3>
              <p className="text-sm text-gray-500 mb-8">{t('contact.formDesc')}</p>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                    <i className="ri-check-double-line text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('contact.successTitle')}</h4>
                  <p className="text-sm text-gray-500 max-w-xs leading-relaxed">{t('contact.successDesc')}</p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-request-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('contact.labelName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder={t('contact.placeholderName')}
                      required
                      className="w-full h-11 border border-gray-200 rounded-lg px-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('contact.labelContact')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      placeholder={t('contact.placeholderContact')}
                      required
                      className="w-full h-11 border border-gray-200 rounded-lg px-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('contact.labelEmail')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.placeholderEmail')}
                      required
                      className="w-full h-11 border border-gray-200 rounded-lg px-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('contact.labelService')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full h-11 border border-gray-200 rounded-lg px-4 text-sm text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 bg-white cursor-pointer"
                    >
                      <option value="">{t('contact.placeholderService')}</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t('contact.labelComment')}
                      <span className="text-gray-400 font-normal ml-1">({formData.comment.length}/500)</span>
                    </label>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      placeholder={t('contact.placeholderComment')}
                      rows={4}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 resize-none"
                    />
                    {formData.comment.length > 500 && (
                      <p className="text-xs text-red-500 mt-1">{t('contact.maxLength')}</p>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                      <i className="ri-error-warning-line text-red-500 text-sm"></i>
                      <p className="text-xs text-red-600">{t('contact.errorMsg')}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || formData.comment.length > 500}
                    className="whitespace-nowrap w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg text-sm transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t('contact.btnSending')}
                      </>
                    ) : (
                      <>
                        {t('contact.btnSend')} <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center leading-relaxed">
                    {t('contact.privacy')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        canonical={`${siteUrl}/contact`}
        lang={lang}
      />
      <Header />
      <main>
        <ContactHero />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
